import React, { useMemo, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Truck, CheckCircle, Shield, Wrench, HardHat, Flame, ChevronRight } from 'lucide-react';
import { products } from '../mock';

// Level-1 hierarchy: ONLY these top-level categories
const LEVEL1 = ['Firefighting Vehicles', 'Equipment', 'Fire Protection Systems', 'Firefighting Robots', 'Rescue Tools'];

// Level-2 subcategories (for Firefighting Vehicles & Equipment these map to product.category;
// for Fire Protection Systems these map to product.subcategory)
const SUBCATEGORIES = {
  'Firefighting Vehicles': ['ARFFV', 'Aerial Vehicles', 'Domestic Fire Tenders', 'Emergency Rescue Tender', 'Forest Firefighting Vehicle'],
  Equipment: ['Firefighting Equipment', "Firefighter's Equipment", 'Pumps', 'Accessories'],
  'Fire Protection Systems': [
    'Fire Alarm & Detection Systems',
    'Fire Extinguishing System',
    'Fire Hydrant System',
    'Fire Sprinkler System',
    'Fire Suppression System',
  ],
  'Firefighting Robots': ['Firefighting Robots'],
  'Rescue Tools': ['Rescue Tools'],
};

// Subcategories that do NOT support further subdivision UI (just show products directly)
const NO_SUBDIVISION = new Set(['Rescue Tools', 'Firefighting Robots']);

// For FPS, products are filtered by `subcategory` field instead of `category`
const isFPSLevel1 = (l1) => l1 === 'Fire Protection Systems';

// Display label overrides (Level-2 chips)
const subLabel = (sub) => (sub === 'Domestic Fire Tenders' ? 'DFT' : sub);

// Headline copy per Level-1
const LEVEL1_COPY = {
  'Firefighting Vehicles': {
    title: 'Firefighting Vehicles',
    description: 'Purpose-built fire fighting and rescue vehicles engineered for every operational scenario',
  },
  Equipment: {
    title: 'Equipment',
    description: 'Firefighting equipment, personal protective gear, pumps, and accessories for safety and operational readiness',
  },
  'Fire Protection Systems': {
    title: 'Fire Protection Systems',
    description: 'Complete fire safety, detection, and suppression systems for buildings and facilities',
  },
  'Firefighting Robots': {
    title: 'Firefighting Robots',
    description: 'Advanced robotic solutions for high-risk fire suppression and rescue operations',
  },
  'Rescue Tools': {
    title: 'Rescue Tools',
    description: 'Professional hydraulic and rescue tools for emergency response operations',
  },
};

// Aliases for old URL params (backwards compat)
const SUB_ALIAS = {
  ARFFVs: 'ARFFV',
  DFTs: 'Domestic Fire Tenders',
  'Fire Protection System': 'Fire Protection Systems',
};

// Resolve incoming URL params (supports legacy ?category=ARFFV and new ?category=Vehicles&sub=ARFFV)
const resolveSelection = (search) => {
  const params = new URLSearchParams(search);
  let cat = params.get('category');
  let sub = params.get('sub');
  if (cat && SUB_ALIAS[cat]) cat = SUB_ALIAS[cat];
  if (sub && SUB_ALIAS[sub]) sub = SUB_ALIAS[sub];

  // New format
  if (cat && LEVEL1.includes(cat)) {
    const validSubs = SUBCATEGORIES[cat];
    return { level1: cat, level2: sub && validSubs.includes(sub) ? sub : validSubs[0] };
  }

  // Legacy format: cat is a sub-category. Map back to its parent.
  if (cat) {
    for (const l1 of LEVEL1) {
      if (SUBCATEGORIES[l1].includes(cat)) {
        return { level1: l1, level2: cat };
      }
    }
  }

  // Default
  return { level1: 'Firefighting Vehicles', level2: SUBCATEGORIES['Firefighting Vehicles'][0] };
};

const Products = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { level1, level2 } = useMemo(() => resolveSelection(location.search), [location.search]);

  // Scroll to just below the sticky tabs whenever category or sub-category changes
  // Skip if navigated from search (state scroll handles it)
  const productsBodyRef = useRef(null);
  useEffect(() => {
    if (location.state?.scrollToId) return;
    if (productsBodyRef.current) {
      const top = productsBodyRef.current.getBoundingClientRect().top + window.scrollY - 160;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }
  }, [level1, level2]);

  // Scroll to a specific product when navigated from search (via router state)
  useEffect(() => {
    const scrollToId = location.state?.scrollToId;
    if (!scrollToId) return;
    const timer = setTimeout(() => {
      const el = document.getElementById(scrollToId);
      if (!el) return;
      let docTop = 0;
      let node = el;
      while (node) { docTop += node.offsetTop; node = node.offsetParent; }
      window.scrollTo({ top: Math.max(0, docTop - 170) });
    }, 100);
    return () => clearTimeout(timer);
  }, [location.state?.scrollToId, level1, level2]);

  const selectLevel1 = (l1) => {
    const firstSub = SUBCATEGORIES[l1][0];
    navigate(`/products?category=${encodeURIComponent(l1)}&sub=${encodeURIComponent(firstSub)}`);
  };

  const selectLevel2 = (sub) => {
    navigate(`/products?category=${encodeURIComponent(level1)}&sub=${encodeURIComponent(sub)}`);
  };

  // For Rescue Tools we display products directly (no Level-2 chips). All other Level-1s have chips.
  const hasLevel2UI = !NO_SUBDIVISION.has(level1) && SUBCATEGORIES[level1].length > 1;

  const activeSubCategory = hasLevel2UI ? level2 : SUBCATEGORIES[level1][0];
  const filteredProducts = products.filter((p) =>
    isFPSLevel1(level1)
      ? p.category === 'Fire Protection Systems' && p.subcategory === activeSubCategory
      : p.category === activeSubCategory
  );

  const headline = LEVEL1_COPY[level1];

  return (
    <div className="min-h-screen" data-testid="products-page">
      {/* Hero Section — header always reflects LEVEL 1 */}
      <section className="relative bg-slate-900 text-white py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.08]" style={{
          backgroundImage: 'radial-gradient(circle at 20% 30%, white 1px, transparent 1px), radial-gradient(circle at 80% 70%, white 1px, transparent 1px)',
          backgroundSize: '48px 48px'
        }}></div>
        <div className="absolute left-0 top-0 h-1 w-full bg-red-600"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="eyebrow" style={{ color: '#f4a8b4' }}>Our Products</span>
            <h1 className="font-display text-4xl md:text-6xl font-extrabold mb-5 mt-3 text-balance" data-testid="products-hero-title">
              {headline.title}
            </h1>
            <p className="text-base md:text-lg text-slate-300">{headline.description}</p>
          </div>
        </div>
      </section>

      {/* Breadcrumbs — always start from Level 1 */}
      <section className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center text-sm text-slate-500 flex-wrap gap-1.5" data-testid="breadcrumbs">
            <Link to="/" className="hover:text-red-600 transition-colors">Home</Link>
            <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
            <Link to="/products" className="hover:text-red-600 transition-colors">Products</Link>
            <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
            <Link
              to={`/products?category=${encodeURIComponent(level1)}`}
              className="hover:text-red-600 transition-colors font-semibold text-slate-900"
              data-testid="breadcrumb-level1"
            >
              {level1}
            </Link>
            {hasLevel2UI && (
              <>
                <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
                <span className="text-slate-600" data-testid="breadcrumb-level2">{subLabel(level2)}</span>
              </>
            )}
          </nav>
        </div>
      </section>

      {/* Level-1 Tabs */}
      <section className="py-4 md:py-6 bg-white border-b border-slate-100 sticky top-16 md:top-[88px] z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-3 md:space-y-4">
          <div className="flex gap-2 overflow-x-auto no-scrollbar md:justify-center -mx-4 px-4 md:mx-0 md:px-0" data-testid="level1-tabs">
            {LEVEL1.map((cat) => (
              <button
                key={cat}
                onClick={() => selectLevel1(cat)}
                data-testid={`level1-tab-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-4 md:px-5 py-2.5 rounded-sm font-display font-semibold text-sm md:text-base whitespace-nowrap transition-all border ${
                  level1 === cat
                    ? 'bg-red-600 text-white border-red-600 shadow-sm'
                    : 'bg-white text-slate-700 border-slate-200 hover:border-red-600 hover:text-red-600'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Level-2 Sub-chips (only for Vehicles & Equipment) */}
          {hasLevel2UI && (
            <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 md:justify-center -mx-4 px-4 md:mx-0 md:px-0" data-testid="level2-tabs">
              {SUBCATEGORIES[level1].map((sub) => (
                <button
                  key={sub}
                  onClick={() => selectLevel2(sub)}
                  data-testid={`level2-tab-${sub.toLowerCase().replace(/['\s]+/g, '-')}`}
                  className={`px-4 py-1.5 rounded-full text-xs md:text-sm font-medium whitespace-nowrap transition-all border ${
                    level2 === sub
                      ? 'bg-slate-900 text-white border-slate-900'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-red-600 hover:text-red-600'
                  }`}
                >
                  {subLabel(sub)}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Products Body */}
      <div ref={productsBodyRef}>
      {level1 === 'Fire Protection Systems' ? (
        /* Fire Protection Systems — chips filter (single sub-category at a time) */
        <section className="py-16 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {filteredProducts.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Shield className="h-10 w-10 text-red-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">{activeSubCategory}</h2>
                <p className="text-slate-600">Products in this sub-category are coming soon.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {filteredProducts.map((product) => (
                  <Card key={product.id} className="border-none shadow-md hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">{product.name}</h3>
                      <p className="text-sm text-slate-600 mb-4">{product.description}</p>
                      <div className="space-y-1">
                        {product.features.slice(0, 2).map((feature, idx) => (
                          <div key={idx} className="flex items-start gap-2 text-xs text-slate-600">
                            <CheckCircle className="h-3 w-3 text-green-600 mt-0.5 flex-shrink-0" />
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </section>
      ) : level1 === 'Rescue Tools' ? (
        /* Rescue Tools grid */
        <section className="py-16 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.filter((p) => p.category === 'Rescue Tools').map((product) => (
                <Card key={product.id} className="border-none shadow-md hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">{product.name}</h3>
                    <p className="text-sm text-slate-600 mb-4">{product.description}</p>
                    <div className="space-y-1.5">
                      {product.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                          <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      ) : filteredProducts.length === 0 ? (
        /* Empty state */
        <section className="py-24 bg-slate-50">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Wrench className="h-10 w-10 text-red-600" />
            </div>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">{subLabel(activeSubCategory)}</h2>
            <p className="text-lg text-slate-600 mb-8">
              Products in this category are coming soon. We are constantly expanding our portfolio to meet your fire safety needs.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white">
                Contact Us for Details
              </Button>
            </Link>
          </div>
        </section>
      ) : (
        /* Vehicles & Equipment — products grid for active sub-category */
        <section className="py-16 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProducts.map((product) => {
                const isEquipment = product.category === "Firefighter's Equipment" || product.category === 'Firefighting Equipment';
                const isPump = product.category === 'Pumps';
                const isAccessory = product.category === 'Accessories';
                const isAerialVehicle = product.category === 'Aerial Vehicles';
                const useLightBg = isPump || isAccessory;
                return (
                  <Card id={`product-${product.id}`} key={product.id} className="overflow-hidden bg-white border border-slate-200 rounded-md shadow-none hover:shadow-xl hover:-translate-y-1 hover:border-slate-300 transition-all duration-300 group flex flex-col">
                    <div className={`${useLightBg ? (isAccessory ? 'h-80' : 'h-72') + ' bg-white border-b border-slate-100' : isEquipment ? 'h-56 bg-gradient-to-br from-slate-100 to-slate-200' : isAerialVehicle ? 'h-80 bg-slate-900' : 'h-56 bg-gradient-to-br from-slate-800 to-slate-700'} flex items-center justify-center relative overflow-hidden`}>
                      {product.image ? (
                        <img
                          src={product.image}
                          alt={product.name}
                          className={`${useLightBg || isEquipment || isAerialVehicle ? 'max-h-full max-w-full object-contain' : 'w-full h-full object-cover'} group-hover:scale-105 transition-transform duration-300`}
                        />
                      ) : product.category === "Firefighter's Equipment" ? (
                        <HardHat className="h-24 w-24 text-slate-400 group-hover:scale-110 transition-transform duration-300" />
                      ) : product.category === 'Firefighting Equipment' ? (
                        <Flame className="h-24 w-24 text-slate-400 group-hover:scale-110 transition-transform duration-300" />
                      ) : (
                        <Truck className="h-24 w-24 text-white opacity-50 group-hover:scale-110 transition-transform duration-300" />
                      )}
                      <Badge className="absolute top-4 right-4 bg-red-600 text-white font-medium">{subLabel(product.category)}</Badge>
                    </div>
                    <CardContent className="p-6 flex flex-col flex-grow">
                      <h3 className="font-display text-xl font-bold text-slate-900 mb-3">{product.name}</h3>
                      {product.description && (
                        <p className="text-slate-600 mb-5 text-sm leading-relaxed">{product.description}</p>
                      )}

                      <div className="space-y-2 mb-6 flex-grow">
                        {product.features && product.features.length > 0 && (
                          <>
                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-3">Key Features</p>
                            {product.features.map((feature, idx) => (
                              <div key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                                <CheckCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                                <span>{feature}</span>
                              </div>
                            ))}
                          </>
                        )}
                      </div>

                      <Link to="/contact" className="mt-auto">
                        <Button className="w-full bg-slate-900 hover:bg-red-600 text-white whitespace-nowrap rounded-sm h-11 transition-colors">
                          Request More Information
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
      )}
      </div>

      {/* CTA Section */}
      <section className="relative py-24 bg-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07]" style={{
          backgroundImage: 'radial-gradient(circle at 20% 30%, white 1px, transparent 1px), radial-gradient(circle at 80% 70%, white 1px, transparent 1px)',
          backgroundSize: '48px 48px'
        }}></div>
        <div className="absolute left-0 top-0 h-1 w-full bg-red-600"></div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="eyebrow" style={{ color: '#f4a8b4' }}>Custom Engineering</span>
          <h2 className="font-display text-3xl md:text-5xl font-extrabold mb-5 mt-3 text-balance">Need a Custom Solution?</h2>
          <p className="text-base md:text-lg mb-10 max-w-2xl mx-auto text-slate-300">
            Our experts can design and deliver tailored fire and safety solutions for your specific requirements.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-10 h-14 text-base rounded-sm">
              Contact Our Experts
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Products;
